/**
 * Request origin for Auth.js / client-portal redirects.
 *
 * AUTH_URL (or NEXTAUTH_URL) is pinned to https://evergreenlifeinsurance.com
 * in SWA app settings (Entra callbacks; the 2026-07-14 outage). next-auth's
 * `reqWithEnvURL` then rewrites `req.nextUrl` to that origin — including
 * on `*.azurestaticapps.net` PR previews. Next.js middleware requires an
 * absolute Location, so /account gates are relocated onto the incoming
 * preview / local origin — never AUTH_URL. Magic-link emails use the
 * same host.
 *
 * Production SWA is different: the Node API sees Host `localhost:8080`.
 * That listener is not the browser. Prefer AUTH_URL there so Auth.js
 * never mints `__Secure-authjs.callback-url=https://localhost:8080/…`.
 */

export const CANONICAL_PRODUCTION_ORIGIN = "https://evergreenlifeinsurance.com";
export const CANONICAL_PRODUCTION_HOST = "evergreenlifeinsurance.com";

export type RequestHostHeaders = {
  host?: string | null;
  forwardedHost?: string | null;
  forwardedProto?: string | null;
  /** SWA `x-ms-original-url` / `x-original-url` — the browser URL. */
  originalUrl?: string | null;
  /** `x-original-host` when a proxy rewrites Host. */
  originalHost?: string | null;
  /** Used only when no browser Host is present. */
  referer?: string | null;
  /**
   * Stamped by middleware from the *unwrapped* request origin before
   * Auth.js rewrites nextUrl to AUTH_URL.
   */
  stampedOrigin?: string | null;
  /** SWA app setting. Used only when no usable Host is present. */
  authUrl?: string | null;
};

function firstHost(value: string | null | undefined): string | null {
  if (!value) return null;
  const host = value.split(",")[0]?.trim().toLowerCase();
  return host || null;
}

function hostnameOf(host: string): string {
  return host.replace(/:\d+$/, "");
}

function hostFromUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  try {
    return firstHost(new URL(value).host);
  } catch {
    return firstHost(value);
  }
}

export function isCanonicalProductionHost(
  host: string | null | undefined,
): boolean {
  const name = hostnameOf(firstHost(host) ?? "");
  return (
    name === CANONICAL_PRODUCTION_HOST ||
    name.endsWith(`.${CANONICAL_PRODUCTION_HOST}`)
  );
}

export function isAzureStaticWebAppsHost(
  host: string | null | undefined,
): boolean {
  const name = hostnameOf(firstHost(host) ?? "");
  return name === "azurestaticapps.net" || name.endsWith(".azurestaticapps.net");
}

export function isAzureWebsitesHost(host: string | null | undefined): boolean {
  const name = hostnameOf(firstHost(host) ?? "");
  return name === "azurewebsites.net" || name.endsWith(".azurewebsites.net");
}

export function isLocalDevHost(host: string | null | undefined): boolean {
  const name = hostnameOf(firstHost(host) ?? "");
  return (
    name === "localhost" ||
    name === "127.0.0.1" ||
    name === "[::1]" ||
    name === "0.0.0.0"
  );
}

/**
 * SWA's managed Node API listens on loopback :8080. That Host is not the
 * browser origin. Local `next dev` is typically :3000.
 */
export function isInternalSwaListenerHost(
  host: string | null | undefined,
): boolean {
  const raw = firstHost(host);
  return Boolean(raw && isLocalDevHost(raw) && /:8080$/.test(raw));
}

/** AUTH_URL, then NEXTAUTH_URL (Auth.js v4 name still honored). */
export function configuredAuthUrl(
  authUrl?: string | null,
  nextAuthUrl?: string | null,
): string | null {
  const primary = authUrl === undefined ? process.env.AUTH_URL : authUrl;
  const fallback =
    nextAuthUrl === undefined ? process.env.NEXTAUTH_URL : nextAuthUrl;
  const raw = primary?.trim() || fallback?.trim() || "";
  return raw || null;
}

/** Preview / local hosts that must never be rewritten to AUTH_URL. */
export function isNonProductionRequestHost(
  host: string | null | undefined,
): boolean {
  return isAzureStaticWebAppsHost(host) || isLocalDevHost(host);
}

/** Hosts Auth.js / this app may emit for first-party Location headers. */
export function isSameAppRedirectHost(
  host: string | null | undefined,
): boolean {
  return (
    isCanonicalProductionHost(host) ||
    isAzureStaticWebAppsHost(host) ||
    isLocalDevHost(host) ||
    isAzureWebsitesHost(host)
  );
}

function originForHost(host: string, proto: string): string {
  const scheme = proto.replace(/:$/, "") || "https";
  return `${scheme}://${host}`;
}

function protoFrom(
  forwardedProto: string | null | undefined,
  host: string,
): string {
  const proto = firstHost(forwardedProto);
  if (proto) return proto;
  if (isLocalDevHost(host)) return "http";
  return "https";
}

function browserHostCandidates(input: RequestHostHeaders): string[] {
  const values = [
    hostFromUrl(input.originalUrl),
    hostFromUrl(input.stampedOrigin),
    firstHost(input.originalHost),
    firstHost(input.forwardedHost),
    firstHost(input.host),
  ].filter((value): value is string => Boolean(value));

  const seen = new Set<string>();
  const hosts: string[] = [];
  for (const host of values) {
    if (seen.has(host) || isAzureWebsitesHost(host)) continue;
    seen.add(host);
    hosts.push(host);
  }
  return hosts;
}

/**
 * Headers Auth.js / SWA actually expose. Prefer these over `req.nextUrl`
 * — `reqWithEnvURL` rewrites that to AUTH_URL.
 */
export function requestHostHeadersFrom(
  headerList: { get(name: string): string | null },
  authUrl: string | null | undefined = configuredAuthUrl(),
): RequestHostHeaders {
  return {
    host: headerList.get("host"),
    forwardedHost: headerList.get("x-forwarded-host"),
    forwardedProto: headerList.get("x-forwarded-proto"),
    originalUrl:
      headerList.get("x-ms-original-url") ?? headerList.get("x-original-url"),
    originalHost: headerList.get("x-original-host"),
    referer: headerList.get("referer"),
    stampedOrigin: headerList.get("x-eg-browser-origin"),
    authUrl,
  };
}

function configuredPublicOrigin(
  authUrl: string | null | undefined,
): string | null {
  return originFromAuthUrl(configuredAuthUrl(authUrl));
}

/**
 * Loopback Host is the SWA Node listener (not the browser) when AUTH_URL
 * is the public production origin. True local Next (`localhost:3000` / http)
 * stays on localhost even if AUTH_URL is pinned to production.
 */
export function isSwaInternalLoopbackHost(
  host: string | null | undefined,
  authUrl?: string | null,
  forwardedProto?: string | null,
): boolean {
  if (!isLocalDevHost(host)) return false;
  const publicOrigin = configuredPublicOrigin(authUrl);
  if (!publicOrigin || !isCanonicalProductionHost(new URL(publicOrigin).host)) {
    return false;
  }
  if (isInternalSwaListenerHost(host)) return true;
  return firstHost(forwardedProto) === "https";
}

/**
 * Browser-facing origin from Host / SWA headers only. Does not fall back
 * to AUTH_URL — that env is pinned to production on every SWA slot.
 * Internal SWA `localhost:8080` is skipped when AUTH_URL is public so
 * callers can prefer the configured origin instead of the Node listener.
 */
export function tryOriginFromRequestHeaders(
  input: RequestHostHeaders,
): string | null {
  const candidates = browserHostCandidates(input);
  const usable = candidates.filter(
    (host) =>
      !isSwaInternalLoopbackHost(host, input.authUrl, input.forwardedProto),
  );

  const preview = usable.find((host) => isAzureStaticWebAppsHost(host));
  if (preview) {
    return originForHost(preview, protoFrom(input.forwardedProto, preview));
  }

  const production = usable.find((host) => isCanonicalProductionHost(host));
  if (production) return CANONICAL_PRODUCTION_ORIGIN;

  const referer = hostFromUrl(input.referer);
  if (referer && isAzureStaticWebAppsHost(referer)) {
    return originForHost(referer, protoFrom(input.forwardedProto, referer));
  }

  const local = usable.find((host) => isLocalDevHost(host));
  if (local) {
    return originForHost(local, protoFrom(input.forwardedProto, local));
  }

  if (referer && isNonProductionRequestHost(referer)) {
    return originForHost(referer, protoFrom(input.forwardedProto, referer));
  }

  if (usable[0]) {
    return originForHost(
      usable[0],
      protoFrom(input.forwardedProto, usable[0]),
    );
  }

  return null;
}

function originFromAuthUrl(authUrl: string | null | undefined): string | null {
  const raw = authUrl?.trim();
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    if (isCanonicalProductionHost(parsed.host)) {
      return CANONICAL_PRODUCTION_ORIGIN;
    }
    if (isNonProductionRequestHost(parsed.host)) {
      return `${parsed.protocol}//${parsed.host}`;
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Resolve the browser-facing origin for this request (magic-link emails).
 * SWA preview hosts win over AUTH_URL. The SWA Node listener
 * (`localhost:8080`) does not — AUTH_URL / NEXTAUTH_URL wins there.
 */
export function originFromRequestHeaders(input: RequestHostHeaders): string {
  return (
    tryOriginFromRequestHeaders(input) ??
    originFromAuthUrl(configuredAuthUrl(input.authUrl)) ??
    CANONICAL_PRODUCTION_ORIGIN
  );
}

function isUsableNonProductionOrigin(
  origin: string,
  input: RequestHostHeaders,
): boolean {
  try {
    const host = new URL(origin).host;
    if (isAzureStaticWebAppsHost(host)) return true;
    if (
      isLocalDevHost(host) &&
      !isSwaInternalLoopbackHost(host, input.authUrl, input.forwardedProto)
    ) {
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Origin for middleware Location headers. Next.js requires an absolute
 * URL; Auth.js has already rewritten `nextUrl` to AUTH_URL on the wrapped
 * request, so pass the *unwrapped* `req.nextUrl.origin` as `requestOrigin`.
 * Do not use that unwrapped origin when it is the SWA Node listener.
 */
export function resolveBrowserOrigin(
  input: RequestHostHeaders,
  requestOrigin: string,
): string {
  const fromHeaders = tryOriginFromRequestHeaders(input);
  if (fromHeaders && isUsableNonProductionOrigin(fromHeaders, input)) {
    return fromHeaders;
  }
  try {
    if (isUsableNonProductionOrigin(requestOrigin, input)) {
      return requestOrigin;
    }
  } catch {
    // Fall through.
  }
  if (fromHeaders) return fromHeaders;
  try {
    if (
      requestOrigin &&
      !isSwaInternalLoopbackHost(
        new URL(requestOrigin).host,
        input.authUrl,
        input.forwardedProto,
      )
    ) {
      return new URL(requestOrigin).origin;
    }
  } catch {
    // Fall through.
  }
  return (
    originFromAuthUrl(configuredAuthUrl(input.authUrl)) ??
    CANONICAL_PRODUCTION_ORIGIN
  );
}

/**
 * Next.js middleware rejects relative Location (Invalid URL). Keep
 * first-party hops on `requestOrigin`; leave Entra authorize URLs alone.
 */
export function relocateSameAppUrl(
  location: string,
  requestOrigin: string,
): string {
  if (!location) return requestOrigin;
  if (location.startsWith("/") && !location.startsWith("//")) {
    return new URL(location, requestOrigin).toString();
  }
  try {
    const parsed = new URL(location);
    if (isSameAppRedirectHost(parsed.host)) {
      return new URL(
        `${parsed.pathname}${parsed.search}${parsed.hash}`,
        requestOrigin,
      ).toString();
    }
    return location;
  } catch {
    return new URL("/", requestOrigin).toString();
  }
}

export function buildGateRedirectPath(decision: {
  pathname: string;
  callbackUrl?: string;
}): string {
  const dest = new URL(decision.pathname, CANONICAL_PRODUCTION_ORIGIN);
  if (decision.callbackUrl) {
    dest.searchParams.set("callbackUrl", decision.callbackUrl);
  }
  return `${dest.pathname}${dest.search}`;
}

export function buildGateRedirectUrl(
  decision: { pathname: string; callbackUrl?: string },
  origin: string,
): string {
  return new URL(buildGateRedirectPath(decision), origin).toString();
}

/**
 * Collapse first-party absolute Locations to a path so AUTH_URL / a
 * prod-pinned x-forwarded-host cannot take the browser off this host.
 * Leave Entra and other external Locations alone.
 */
export function toSameAppBrowserLocation(location: string): string {
  if (!location) return location;
  if (location.startsWith("/") && !location.startsWith("//")) return location;
  try {
    const parsed = new URL(location);
    if (isSameAppRedirectHost(parsed.host)) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return location;
  } catch {
    return location;
  }
}

/**
 * Preview hosts must receive a relative Location or one that stays on
 * that same azurestaticapps.net / localhost origin — never production.
 */
export function isPreviewSafeRedirectLocation(
  location: string,
  host: string,
): boolean {
  if (!isNonProductionRequestHost(host)) return true;
  if (/evergreenlifeinsurance\.com/i.test(location)) return false;
  if (location.startsWith("/") && !location.startsWith("//")) return true;
  try {
    const parsed = new URL(location);
    return hostnameOf(parsed.host) === hostnameOf(firstHost(host) ?? "");
  } catch {
    return false;
  }
}

/**
 * Auth.js `redirect` callback. Path-absolute and same-app URLs stay
 * path-absolute so SWA preview never follows AUTH_URL to production.
 * External URLs (Entra authorize) pass through.
 */
export function resolveAuthRedirectUrl(
  url: string,
  requestOrigin: string,
): string {
  if (url.startsWith("/") && !url.startsWith("//")) {
    return url;
  }
  try {
    const parsed = new URL(url);
    if (isSameAppRedirectHost(parsed.host)) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    return parsed.toString();
  } catch {
    try {
      return new URL("/", requestOrigin).pathname;
    } catch {
      return "/";
    }
  }
}
